/**
 * 将错误上报至arms或者其他相关跟踪信息文件
 * 阿里云Arms错误收集平台：https://arms.console.aliyun.com/retcode
 * 错误收集文档：https://help.aliyun.com/zh/arms/browser-monitoring/developer-reference/api-reference
 */
import { ProtectionJurisdictions, getCookieConsent } from '@/components/Layout/CookieBot/CookieConsent.config';
import { Arms } from '@/components/Layout/ScriptSnippets';
import { feishuRobotSend } from '../feishuRobot';

type Payload = {
  filename: string;
  data?: Record<string, unknown> | Array<unknown> | null;
};

// 加载SDK，同时启动轮训监听
const loadArmsSDK = () => {
  const country = window?.country || {};

  Arms({ country });
};

/**
 * 存在政策选择，则以cookie中选择的情况为准
 * 不存在政策选择，以国家为准
 * 没有国家信息，也没有政策选择，不上报
 */
function canCookieControlInitArmsSnippet(force: boolean) {
  const { CookieBotAuth, CookieInsAuth } = getCookieConsent();
  const consent = CookieInsAuth ?? CookieBotAuth ?? {};
  const country = window?.country || {};

  if (
    force ||
    // @ts-expect-error Record<'preferences' | 'satistics' | 'marketing'>
    consent?.preferences ||
    // @ts-expect-error Record<'preferences' | 'satistics' | 'marketing'>
    (typeof consent?.preferences !== 'boolean' && !ProtectionJurisdictions.includes(country.code))
  ) {
    return true;
  }
  return false;
}

// sdk还未加载时候的上报处理，如果强制执行则加载SDK
export const reportBeforeArmsSdkInit = (force: boolean) => {
  if (canCookieControlInitArmsSnippet(force)) {
    loadArmsSDK();
  }
};

/**
 * 错误主动上报方法
 * @param {Error} error Error类型
 * @param {boolean} force 是否强制上报（如果没有下载sdk会直接下载）
 * @param {Object} payload 错误上报携带数据 最好上传
 * - filename 当前错误的文件名，必须携带，展示在JS文件URL 一栏中
 * - data{object|array} 需要上报的错误数据，将会携带在自定义数据中
 *
 * =====================
 * 参考使用：
 * sendArmsError(Error,{filename:'current filename',data:{}})
 * =====================
 */
export function armsErrorSend(error: Error | string | null, payload: Payload = { filename: '' }, force = false) {
  if (typeof window === 'undefined' || !error || !window.monitorCore) return;
  try {
    reportBeforeArmsSdkInit(force);

    const properties =
      typeof payload.data === 'object' ? (Array.isArray(payload.data) ? { data: payload.data } : payload) : {};

    window.monitorCore.sendException(error, {
      filename: payload?.filename || '',
      properties: properties ?? {},
    });
  } catch (e) {
    feishuRobotSend({
      text: ['RUM armsErrorSend 数据发送错误', (e as Error)?.stack!.toString()],
      err: e as Error,
      at: ['dxy'],
    });
  }
}

export type TSendRumEventPayload = {
  /** 事件类型（可取当前功能名） */
  type: string;
  /** 事件名称 */
  name: string;
  /** 事件分组，以功能模块划分 */
  group?: `${RUM_EVENT_GROUP}`;
  /** 值 */
  value?: number;
  /** 自定义携带信息 */
  data?: Record<string, unknown>;
};

/**
 * 事件主动上报方法
 * @property {string} type - 事件类型
 * @property {string} name - 事件名称
 * @property {RUM_EVENT_GROUP} [group] - 事件分组，默认为other
 * @property {number} [value] - 值，可选
 * @property {Record<string, unknown>} data - 自定义属性对象
 */
export function sendRumEvent(payload: TSendRumEventPayload, force = false) {
  if (typeof window === 'undefined' || !payload || !window.monitorCore) return;
  const group = (payload.group && RUM_EVENT_GROUP[payload.group]) ?? RUM_EVENT_GROUP.OTHER;
  try {
    reportBeforeArmsSdkInit(force);

    const properties =
      typeof payload.data === 'object' ? (Array.isArray(payload.data) ? { data: payload.data } : payload) : {};

    window.monitorCore.sendEvent({
      ...payload,
      group,
      properties,
    });
  } catch (e) {
    feishuRobotSend({
      text: ['RUM sendRumEvent 数据发送错误', (e as Error)?.stack!.toString()],
      err: e as Error,
      at: ['dxy'],
    });
  }
}

/** RUM自定义事件的分组，以功能模块为划分 */
export enum RUM_EVENT_GROUP {
  /** 首页 */
  HOME = 'HOME',
  /** 搜索页 */
  SEARCH = 'SEARCH',
  /** 导航 */
  NAV = 'NAV',
  /** 产品 */
  PRODUCT = 'PRODUCT',
  /** 购物车 */
  CART = 'CART',
  /** 订单 */
  ORDER = 'ORDER',
  /** BI */
  BI = 'BI',
  /** 分销 */
  AFFILIATE = 'AFFILIATE',
  /** 以旧换新 */
  TRADEUP = 'TRADEUP',
  /** 活动 */
  CAMPAIGN = 'CAMPAIGN',
  /** 类目页 */
  CATEGORY_PAGE = 'CATEGORY_PAGE',
  /** 云存 */
  CLOUD_SERVICE = 'CLOUD_SERVICE',
  /** 支付 */
  PAYMENT = 'PAYMENT',
  /** FAQ */
  FAQ = 'FAQ',
  /** utils 工具 */
  UTILS = 'UTILS',
  /** 其他 */
  OTHER = 'OTHER',
}
